/** Application chrome: header, primary nav, and the outlet pages render into. */

import { clearStoredToken, requireToken } from "../api/auth";
import { el } from "../lib/dom";
import { homePath, newNotePath, searchPath } from "../router/paths";
import { navigate } from "../router/router";

export interface Shell {
  readonly root: HTMLElement;
  readonly outlet: HTMLElement;
  readonly setActivePath: (path: string) => void;
}

interface NavEntry {
  readonly label: string;
  readonly href: string;
  /** Path prefixes that should light this entry up. */
  readonly matches: readonly string[];
}

const NAV_ENTRIES: readonly NavEntry[] = [
  { label: "Notes", href: homePath(), matches: ["/"] },
  { label: "Search", href: searchPath(), matches: ["/search"] },
];

function isActive(entry: NavEntry, path: string): boolean {
  return entry.matches.some((prefix) => (prefix === "/" ? path === "/" : path.startsWith(prefix)));
}

export function createShell(): Shell {
  const links = NAV_ENTRIES.map((entry) =>
    el("a", { className: "nav__link", attrs: { href: entry.href }, text: entry.label }),
  );

  const nav = el("nav", {
    className: "nav",
    attrs: { "aria-label": "Primary" },
    children: links,
  });

  const tokenButton = el("button", {
    className: "button button--ghost",
    attrs: { type: "button", title: "Replace the stored API token" },
    text: "Token",
    on: {
      click: (): void => {
        clearStoredToken();
        void requireToken()
          .then(() => {
            navigate(window.location.hash === "" ? homePath() : window.location.hash);
          })
          .catch(() => undefined);
      },
    },
  });

  const header = el("header", {
    className: "app__header",
    children: [
      el("a", { className: "brand", attrs: { href: homePath() }, text: "vineelernote" }),
      nav,
      el("div", {
        className: "app__actions",
        children: [
          tokenButton,
          el("a", { className: "button button--primary", attrs: { href: newNotePath() }, text: "New note" }),
        ],
      }),
    ],
  });

  const outlet = el("main", { className: "app__main", attrs: { id: "main" } });

  const root = el("div", { className: "app", children: [header, outlet] });

  return {
    root,
    outlet,
    setActivePath(path: string): void {
      NAV_ENTRIES.forEach((entry, index) => {
        const link = links[index];
        if (link === undefined) {
          return;
        }
        const active = isActive(entry, path);
        link.classList.toggle("nav__link--active", active);
        if (active) {
          link.setAttribute("aria-current", "page");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    },
  };
}
